import { CreditCard, Eye, Heart, PackageCheck, Plus, Star } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";

export default function ProductCard({ product, showBuyNow = false }) {
  const { addToCart, toggleFavorite, favorites, getDisplayPrice } = useShop();
  const { isCorporate, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [buying, setBuying] = useState(false);

  const fav = favorites.includes(product.id);
  const displayPrice = getDisplayPrice(product);
  const stock = Number(product.stock || 0);
  const reviewAverage = Number(product.reviewSummary?.average || 0);
  const reviewCount = Number(product.reviewSummary?.count || 0);
  const roundedRating = Math.round(reviewAverage);

  const cover =
    product.image ||
    product.images?.[0] ||
    "https://images.unsplash.com/photo-1593618998160-e34014e67546?auto=format&fit=crop&w=900&q=80";

  const buyNow = async () => {
    setBuying(true);
    const added = await addToCart(product);
    setBuying(false);
    if (added) navigate("/checkout");
  };

  return (
    <article className="group h-full overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-stone-300 hover:shadow-xl">
      <Link to={`/products/${product.id}`} className="relative block overflow-hidden bg-stone-100">
        <img
          src={cover}
          alt={product.name}
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = "/product-knife-catalog.png";
          }}
          className="h-64 w-full object-cover transition duration-500 group-hover:scale-[1.035] sm:h-72 lg:h-[320px] xl:h-[340px]"
        />

        {product.discount && (
          <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-stone-900 shadow-sm">
            %{product.discount} İndirim
          </span>
        )}

        <span className="absolute bottom-4 left-4 flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-xs font-bold text-stone-900 shadow-sm backdrop-blur">
          <Star size={14} className="text-amber-500" fill="currentColor" />
          {reviewCount ? reviewAverage.toFixed(1) : "Yeni"}
          <span className="font-semibold text-stone-500">({reviewCount})</span>
        </span>

        {!isAdmin && (
          <button
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              toggleFavorite(product.id);
            }}
            aria-label={fav ? "Favorilerden çıkar" : "Favorilere ekle"}
            className={`absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full border border-stone-200 shadow-sm transition ${
              fav ? "bg-red-500 text-white" : "bg-white text-red-500 hover:bg-red-500 hover:text-white"
            }`}
          >
            <Heart size={20} fill={fav ? "currentColor" : "none"} />
          </button>
        )}
      </Link>

      <div className="p-4 sm:p-5">
        <div className="mb-2 flex items-center justify-between gap-3 text-xs font-semibold text-stone-400">
          <span>{product.code || product.id}</span>
          <span className={stock > 0 ? "text-emerald-700" : "text-red-600"}>
            {stock > 0 ? `${stock} stok` : "Stok yok"}
          </span>
        </div>

        <Link
          to={`/products/${product.id}`}
          className="block min-h-[48px] text-[17px] font-semibold leading-snug text-stone-950 transition hover:text-stone-600"
        >
          {product.name}
        </Link>

        <div className="mt-3 flex items-center justify-between gap-3 border-y border-stone-100 py-3">
          <div className="flex items-center gap-1 text-amber-500">
            {[1, 2, 3, 4, 5].map((value) => (
              <Star key={value} size={14} fill={value <= roundedRating ? "currentColor" : "none"} />
            ))}
          </div>
          <span className="text-xs font-semibold text-stone-500">
            {reviewCount ? `${reviewCount} yorum` : "Yorum yok"}
          </span>
        </div>

        <div className="mt-3">
          <p className="text-[17px] font-bold text-stone-950">
            ₺ {Number(displayPrice).toLocaleString("tr-TR")}
          </p>

          {isCorporate && (
            <p className="mt-1 text-sm text-stone-400 line-through">
              ₺ {Number(product.price).toLocaleString("tr-TR")}
            </p>
          )}
        </div>

        {isCorporate && (
          <p className="mt-2 text-xs font-medium text-stone-500">
            Kurumsal hesaplarda minimum 10 adet
          </p>
        )}

        {isAdmin && (
          <p className="mt-2 text-xs font-medium text-amber-700">
            Admin görünümü · Stok: {stock}
          </p>
        )}

        {!isAdmin && stock > 0 && (
          <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-emerald-700">
            <PackageCheck size={15} />
            Hızlı teslimata uygun
          </p>
        )}

        <div className="mt-4">
          {!isAdmin ? (
            <div className="flex flex-col items-center gap-3">
              <button
                disabled={stock <= 0}
                onClick={() => addToCart(product)}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-stone-300 px-4 py-3 text-[16px] font-medium text-stone-950 transition hover:border-red-600 hover:text-red-600 disabled:cursor-not-allowed disabled:border-stone-200 disabled:text-stone-400"
              >
                <Plus size={22} strokeWidth={1.8} />
                {stock <= 0 ? "Stok Yok" : "Sepete Ekle"}
              </button>

              {showBuyNow && (
                <button
                  disabled={stock <= 0 || buying}
                  onClick={buyNow}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-3 text-[16px] font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-stone-300"
                >
                  <CreditCard size={20} strokeWidth={1.8} />
                  {buying ? "Yönlendiriliyor..." : "Hemen Al"}
                </button>
              )}
            </div>
          ) : (
            <Link
              to={`/products/${product.id}`}
              className="flex items-center gap-2 text-[17px] font-medium text-stone-950 transition hover:text-red-600"
            >
              <Eye size={20} strokeWidth={1.8} />
              Ürünü İncele
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}
